import { Alert } from '@televet/kibble-ui/Alert';
import { Badge, Flex } from '@televet/kibble-ui/chakra';
import { Text } from '@televet/kibble-ui/Text';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { CopyableId } from '@/components/CopyableId';
import { Field } from '@/components/Field';
import { PageContainer } from '@/components/PageContainer';
import { SummaryCard } from '@/components/SummaryCard';
import { trpc } from '@/lib/trpc';
import { ConceptRulesTable } from '../-components/ConceptRulesTable';
import { TIER_COLORS } from '../-lib/types';

const TierCounts = ({ rows }: { rows: Array<{ tier: string | null; count: number }> }) => {
  if (rows.length === 0) {
    return (
      <Text fontSize="sm" color="text.subtle">
        None
      </Text>
    );
  }

  return (
    <Flex gap={2} wrap="wrap" align="center">
      {rows.map((row) => (
        <Flex key={row.tier ?? 'none'} gap={1} align="center">
          <Badge size="sm" fontSize="2xs" colorPalette={TIER_COLORS[row.tier ?? '']}>
            {row.tier ?? 'no tier'}
          </Badge>
          <Text fontSize="sm">{row.count}</Text>
        </Flex>
      ))}
    </Flex>
  );
};

const RouteComponent = () => {
  const { conceptId } = Route.useParams();
  const { data, isLoading, isError, error } = useQuery(
    trpc.petConditions.getConcept.queryOptions({ conceptId })
  );

  if (isLoading) {
    return (
      <PageContainer>
        <Flex justify="center" p={8}>
          <Text variant="subtle">Loading concept…</Text>
        </Flex>
      </PageContainer>
    );
  }

  if (isError || !data) {
    return (
      <PageContainer>
        <Flex direction="column" w="100%" gap={4}>
          <Breadcrumbs
            items={[
              { label: 'Pet Conditions', to: '/pet-conditions' },
              { label: 'Concepts', to: '/pet-conditions/concepts' },
              { label: conceptId },
            ]}
          />
          <Alert
            status="error"
            hideCloseButton
            title="Could not load this concept"
            description={error?.message ?? 'The request failed.'}
          />
        </Flex>
      </PageContainer>
    );
  }

  const { concept } = data;

  return (
    <PageContainer>
      <Flex direction="column" w="100%" gap={4}>
        <Breadcrumbs
          items={[
            { label: 'Pet Conditions', to: '/pet-conditions' },
            { label: 'Concepts', to: '/pet-conditions/concepts' },
            { label: concept.name },
          ]}
        />

        <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
          <Flex direction="column" gap={0.5}>
            <Text fontSize="lg" fontWeight="semibold" letterSpacing="-0.01em">
              {concept.name}
            </Text>
            <Text fontSize="xs" fontFamily="mono" color="text.subtle">
              {concept.slug}
            </Text>
          </Flex>
          <Flex gap={2} align="center">
            {concept.isChronic && (
              <Badge variant="outline" size="sm" fontSize="2xs">
                chronic
              </Badge>
            )}
            <Badge variant="outline" size="sm" fontSize="2xs">
              {concept.kind}
            </Badge>
          </Flex>
        </Flex>

        {/* A missing name and an unreachable catalog look alike; only one is a rule problem. */}
        {!data.taxonomyLookupAvailable && (
          <Alert
            status="warning"
            hideCloseButton
            title="Analyte names could not be looked up"
            description="This deployment has no route to the taxonomy, so rules show their raw ids. A missing name here is not evidence that a rule is broken."
          />
        )}

        <Flex gap={4} wrap="wrap">
          <SummaryCard title="CONCEPT">
            <Field label="Concept ID" minW="160px">
              <CopyableId id={concept.id} label="concept" maxW="140px" />
            </Field>
            <Field label="Species" minW="120px">
              <Text fontSize="sm">
                {concept.appliesToSpecies.length > 0 ? concept.appliesToSpecies.join(', ') : 'Any'}
              </Text>
            </Field>
            <Field label="Active lookback" minW="130px">
              <Text fontSize="sm" color="text.subtle">
                {concept.isChronic
                  ? 'Chronic, never decays'
                  : `${concept.defaultActiveLookbackMonths ?? '—'} months`}
              </Text>
            </Field>
          </SummaryCard>

          <SummaryCard title="EVIDENCE, VOIDED EXCLUDED">
            <Field label="Signals by tier" minW="220px">
              <TierCounts rows={data.signalsByTier} />
            </Field>
            <Field label="Pets by tier, every state" minW="220px">
              <TierCounts rows={data.petsByTier} />
            </Field>
          </SummaryCard>
        </Flex>

        <Flex direction="column" gap={1}>
          <Text fontSize="xs" color="text.subtle" fontWeight="medium">
            ALIASES ({concept.aliases.length})
          </Text>
          {concept.aliases.length === 0 ? (
            <Text fontSize="sm" color="text.subtle">
              This concept has no aliases, so nothing matches it by name.
            </Text>
          ) : (
            <Flex gap={1.5} wrap="wrap">
              {concept.aliases.map((alias) => (
                <Badge key={alias} variant="outline" size="sm" fontSize="2xs">
                  {alias}
                </Badge>
              ))}
            </Flex>
          )}
        </Flex>

        <Flex direction="column" gap={1}>
          <Text fontSize="xs" color="text.subtle" fontWeight="medium">
            RULES ({data.rules.length}), LIVE SIGNAL COUNTS EXCLUDE VOIDED, ALL CLINICS
          </Text>
          {data.rules.length === 0 ? (
            <Text fontSize="sm" color="text.subtle">
              This concept has no rules, so nothing can ever match it.
            </Text>
          ) : (
            <ConceptRulesTable rules={data.rules} />
          )}
        </Flex>
      </Flex>
    </PageContainer>
  );
};

export const Route = createFileRoute('/_authenticated/pet-conditions/concepts/$conceptId')({
  component: RouteComponent,
});
